// UI-only metadata for the grass-comparison tool. Kept separate from
// `grass-characteristics.ts` so the horticultural data stays focused on care
// facts and editorial copy (best-for / pros / cons / install-cost score /
// accent color) lives in one file.
//
// Keyed by the canonical name in GRASS_CHARACTERISTICS.

export interface GrassComparisonUiMeta {
  /** Scientific name, italicized in the table header. */
  latinName: string
  /** One-line summary shown above the pros/cons list. */
  bestFor: string
  /** 2–4 short positive bullets. */
  pros: string[]
  /** 2–4 short negative bullets. */
  cons: string[]
  /** 0–5 install-cost score (5 = cheapest, 0 = most expensive). Mirrors the
   *  design's `cost` trait. Seeded from the design package, supplemented from
   *  extension service guidance where the design lacked an entry. */
  costToInstall: number
  /** Per-grass accent color used in the verdict pill, picker swatch, and
   *  trait-bar fill. Tailwind palette hex values keep this consistent with
   *  the rest of the site. */
  accentColor: string
}

export const GRASS_COMPARISON_UI: Record<string, GrassComparisonUiMeta> = {
  'Kentucky Bluegrass': {
    latinName: 'Poa pratensis',
    bestFor: 'Cool-climate yards with full sun and time to mow.',
    pros: [
      'Self-repairing rhizomes',
      'Cold-hardy through harsh winters',
      'Iconic blue-green color',
    ],
    cons: ['Thirsty', 'Slow to germinate', 'High maintenance ceiling'],
    costToInstall: 4,
    accentColor: '#1a7a8a',
  },
  'Perennial Ryegrass': {
    latinName: 'Lolium perenne',
    bestFor: 'Quick fill-ins or overseeding warm lawns for winter green.',
    pros: ['Germinates in a week', 'Wear-resistant', 'Glossy green color'],
    cons: ['Short-lived in summer heat', 'No self-repair'],
    costToInstall: 5,
    accentColor: '#1d8a3b',
  },
  'Tall Fescue': {
    latinName: 'Festuca arundinacea',
    bestFor: 'Northern and transition-zone yards that want green Oct–May.',
    pros: ['Stays green into winter', 'Cheap to seed', 'Deep roots'],
    cons: ['Hates summer heat', 'Bunch-type, no self-repair'],
    costToInstall: 5,
    accentColor: '#198c4c',
  },
  'Fine Fescue': {
    latinName: 'Festuca rubra / ovina',
    bestFor: 'Shaded northern yards with low-traffic, low-water demands.',
    pros: ['Best cool-season shade tolerance', 'Very low fertilizer needs', 'Drought-resilient'],
    cons: ['Thin under foot traffic', 'Looks ragged when stressed'],
    costToInstall: 5,
    accentColor: '#3aa07a',
  },
  Bermudagrass: {
    latinName: 'Cynodon dactylon',
    bestFor: 'Sunny yards in the Sun Belt that get walked on a lot.',
    pros: ['Heat-proof', 'Self-repairing runners', 'Cheap to seed'],
    cons: ['Goes brown in winter', 'Invades flower beds'],
    costToInstall: 4,
    accentColor: '#0e8c3a',
  },
  Zoysiagrass: {
    latinName: 'Zoysia japonica',
    bestFor: 'Owners who want a thick lawn and accept a slow start.',
    pros: ['Chokes out weeds', 'Plush underfoot', 'Some shade tolerance'],
    cons: ['Slow to fill in', 'Thatchy if not dethatched'],
    costToInstall: 2,
    accentColor: '#0c8a5e',
  },
  'St. Augustinegrass': {
    latinName: 'Stenotaphrum secundatum',
    bestFor: 'Florida and Gulf Coast yards with partial shade.',
    pros: ['Best warm-season shade tolerance', 'Quick to cover (sod)', 'Coarse, soft feel'],
    cons: ['Thirsty', 'Chinch-bug prone', 'Cold kills it'],
    costToInstall: 2,
    accentColor: '#0a8868',
  },
  Centipede: {
    latinName: 'Eremochloa ophiuroides',
    bestFor: 'Sandy Southeast yards. Minimal effort, modest results.',
    pros: ['Low fertilizer needs', 'Tolerates poor soil', 'Slow growth = less mowing'],
    cons: ['Cold-sensitive', "Doesn't recover from damage"],
    costToInstall: 2,
    accentColor: '#7da838',
  },
  Bahiagrass: {
    latinName: 'Paspalum notatum',
    bestFor: 'Acreage and sandy yards where irrigation is impractical.',
    pros: ['Survives drought without irrigation', 'Cheap seed'],
    cons: ['Coarse and open', 'Tall seed heads need frequent mowing'],
    costToInstall: 3,
    accentColor: '#5fa03c',
  },
  'Annual Ryegrass': {
    latinName: 'Lolium multiflorum',
    bestFor: 'Winter color on dormant southern lawns, then gone by summer.',
    pros: ['Germinates in days', 'Cheapest seed there is', 'Instant winter green'],
    cons: ['Dies in summer heat', 'Needs reseeding every fall', 'Competes with spring green-up'],
    costToInstall: 5,
    accentColor: '#22a04e',
  },
  'Mixed Cool-Season Blend': {
    latinName: 'Poa / Lolium / Festuca mix',
    bestFor: 'Northern yards that want a forgiving, all-around lawn.',
    pros: [
      'Balances strengths of each species',
      'Cheap, widely available seed',
      'Adapts to sun and light shade',
    ],
    cons: ['Uneven color and texture', 'Weakest species fades over time'],
    costToInstall: 5,
    accentColor: '#2e8b57',
  },
  Kikuyugrass: {
    latinName: 'Cenchrus clandestinus',
    bestFor: 'Mild coastal Southern California yards that get heavy use.',
    pros: ['Repairs itself fast', 'Handles salt air and drought', 'Stays green most of the year on the coast'],
    cons: ['Aggressively invades beds', 'Classified invasive in some areas', 'Sod or stolons only'],
    costToInstall: 2,
    accentColor: '#4c9a2a',
  },
  Buffalograss: {
    latinName: 'Bouteloua dactyloides',
    bestFor: 'Plains and high-desert yards with low water and low mowing tolerance.',
    pros: ['Native prairie grass', 'Almost no irrigation needed', 'Low mowing frequency'],
    cons: ['Slow to establish', 'Thin under heavy traffic', 'Goes dormant brown in cool weather'],
    costToInstall: 3,
    accentColor: '#8aa84a',
  },
}

const DEFAULT_ACCENT = '#16a34a'

export function getGrassComparisonUi(name: string): GrassComparisonUiMeta {
  return (
    GRASS_COMPARISON_UI[name] ?? {
      latinName: '',
      bestFor: '',
      pros: [],
      cons: [],
      costToInstall: 3,
      accentColor: DEFAULT_ACCENT,
    }
  )
}
